import { UnifiedGroup, UnifiedGroupRestoreStatus } from '../../models/hubPersona/unifiedGroup';
import { PersonType } from '../../models/hubPersona/hubPersona';
import type { EnterpriseGroup } from 'owa-group-crud-contract/lib/schema/groupCrudInfo';
import { GroupAccessType } from 'owa-groups-shared-store/lib/schema/UnifiedGroupsSettingsState';
import {
    UnifiedGroupAccessType,
    UnifiedGroupType,
    UnifiedGroupDetails,
    UnifiedGroupSubscriptionType,
    UnifiedGroupExpiryStatus,
} from '../../../service';
import { makeHubGroupPersonaId, makeHubPersonaId, UnifiedGroupExportStatus } from '../..';
import type { Group } from 'owa-groups-crud-common-components/lib/store/schema/entities';

export function convertToUnifiedGroup(
    unifiedGroup: UnifiedGroupType,
    isMember: boolean
): UnifiedGroup {
    return {
        type: PersonType.Group,
        //For deleted groups, there can be multiple groups with same smtp. So use ExternalDirectoryObjectId.
        hubPersonaId: unifiedGroup.WhenSoftDeletedUtc
            ? makeHubPersonaId(unifiedGroup.ExternalDirectoryObjectId)
            : makeHubGroupPersonaId(
                  unifiedGroup.ExternalDirectoryObjectId,
                  unifiedGroup.SmtpAddress ? unifiedGroup.SmtpAddress.toLowerCase() : ''
              ),
        personaId: undefined,
        displayName: unifiedGroup.DisplayName,
        externalDirectoryObjectId:
            unifiedGroup.ExternalDirectoryObjectId !== ''
                ? unifiedGroup.ExternalDirectoryObjectId
                : unifiedGroup.SmtpAddress
                ? unifiedGroup.SmtpAddress.toLowerCase()
                : '',
        emailAddress: unifiedGroup.SmtpAddress ? unifiedGroup.SmtpAddress.toLowerCase() : '',
        description: unifiedGroup.AdditionalProperties
            ? unifiedGroup.AdditionalProperties.Description
            : '',
        memberCount: unifiedGroup.AdditionalProperties
            ? unifiedGroup.AdditionalProperties.MemberCount
            : 0,
        ownerCount: 0,
        pendingMembershipRequests: 0,
        accessType: unifiedGroup.AccessType,
        isMember: isMember,
        isOwner: unifiedGroup.AdditionalProperties
            ? unifiedGroup.AdditionalProperties.IsOwner
            : false,
        isFavorite: unifiedGroup.IsFavorite,
        relevancyScore: unifiedGroup.ActivityScore,
        isFollowed: null,
        isExpiring: unifiedGroup.ExpiryStatus === UnifiedGroupExpiryStatus.Expiring,
        expiryDateTime: unifiedGroup.ExpirationTimeUtc
            ? new Date(unifiedGroup.ExpirationTimeUtc)
            : null,
        lastVisitedTime: new Date(unifiedGroup.LastVisitedTimeUtc),
        whenSoftDeletedTime: unifiedGroup.WhenSoftDeletedUtc
            ? new Date(unifiedGroup.WhenSoftDeletedUtc)
            : null,
        joinedDateTime: new Date(unifiedGroup.JoinDateUtc),
        isLoading: !unifiedGroup.WhenSoftDeletedUtc,
        isJoinRequestPendingApproval: false,
        joinGroupUrl: null,
        isMembershipDynamic: false,
        allowAddGuests: false,
        restoreStatus: UnifiedGroupRestoreStatus.NONE,
        isSubscriptionEnabled: true,
        mailboxGuid: unifiedGroup.MailboxGuid,
        externalDirectoryOrganizationId: unifiedGroup.ExternalDirectoryOrganizationId,
        exportStatus: UnifiedGroupExportStatus.None,
        exportDownloadLink: undefined,
        routingKey: unifiedGroup.RoutingKey,
        groupKind: unifiedGroup.Kind,
        addMembersForFamilyUrl: undefined,
        leaveGroupForFamilyUrl: undefined,
        mailboxCultureName: undefined,
        autoSubscribeNewMembers: undefined,
        allowExternalSenders: undefined,
        allowExportGroup: undefined,
    };
}

export function convertGroupDetailsToUnifiedGroup(details: UnifiedGroupDetails): UnifiedGroup {
    const joinGroupResource = details.GroupResources.find(resource => resource.Name === 'Join');
    const customAddMemberResources = details.GroupResources.find(
        resource => resource.Name === 'CustomAddMembers'
    );
    const customLeaveGroupResources = details.GroupResources.find(
        resource => resource.Name === 'CustomLeaveGroup'
    );
    return {
        type: PersonType.Group,
        hubPersonaId: makeHubGroupPersonaId(
            details.ExternalDirectoryObjectId,
            details.SmtpAddress ? details.SmtpAddress.toLowerCase() : ''
        ),
        personaId: undefined,
        displayName: details.DisplayName,
        externalDirectoryObjectId:
            details.ExternalDirectoryObjectId !== ''
                ? details.ExternalDirectoryObjectId
                : details.SmtpAddress
                ? details.SmtpAddress.toLowerCase()
                : '',
        emailAddress: details.SmtpAddress ? details.SmtpAddress.toLowerCase() : '',
        description: details.Description,
        memberCount: details.MemberCount,
        ownerCount: details.OwnerCount,
        pendingMembershipRequests: 0,
        accessType: details.AccessType,
        isMember: details.UserGroupRelationship.IsMember || false,
        isOwner: details.UserGroupRelationship.IsOwner || false,
        isFavorite: false,
        relevancyScore: 0,
        isFollowed:
            details.UserGroupRelationship &&
            details.UserGroupRelationship.SubscriptionType === UnifiedGroupSubscriptionType.All,
        isExpiring: details.ExpiryStatus === UnifiedGroupExpiryStatus.Expiring,
        expiryDateTime: details.ExpirationTimeUtc ? new Date(details.ExpirationTimeUtc) : null,
        lastVisitedTime: new Date(),
        whenSoftDeletedTime: null,
        joinedDateTime: new Date(),
        isLoading: true,
        isJoinRequestPendingApproval: details.AdditionalProperties?.IsJoinRequestPendingApproval,
        joinGroupUrl: joinGroupResource ? joinGroupResource.Url : null,
        isMembershipDynamic: details.AdditionalProperties?.IsMembershipDynamic,
        allowAddGuests: details.Capabilities?.AllowAddGuests,
        restoreStatus: UnifiedGroupRestoreStatus.NONE,
        isSubscriptionEnabled: details.Capabilities?.SubscriptionEnabled,
        mailboxGuid: details.MailboxGuid,
        externalDirectoryOrganizationId: details.ExternalDirectoryOrganizationId,
        exportStatus: UnifiedGroupExportStatus.None,
        exportDownloadLink: undefined,
        routingKey: undefined,
        groupKind: details.Kind,
        addMembersForFamilyUrl: customAddMemberResources ? customAddMemberResources.Url : undefined,
        leaveGroupForFamilyUrl: customLeaveGroupResources
            ? customLeaveGroupResources.Url
            : undefined,
        mailboxCultureName: details.MailboxSettings.MailboxCultureName,
        autoSubscribeNewMembers: details.MailboxSettings.AutoSubscribeNewMembers,
        allowExternalSenders: details.MailboxSettings.ExternalSendersEnabled,
        allowExportGroup: details.Permissions.CanExportContent,
    };
}

export function convertEnterpriseGroupToUnifiedGroup(group: EnterpriseGroup): UnifiedGroup {
    return {
        type: PersonType.Group,
        hubPersonaId: makeHubGroupPersonaId(
            group.externalDirectoryObjectId,
            group.smtpAddress ? group.smtpAddress.toLowerCase() : ''
        ),
        personaId: undefined,
        displayName: group.groupName,
        externalDirectoryObjectId: group.externalDirectoryObjectId,
        emailAddress: group.smtpAddress ? group.smtpAddress.toLowerCase() : '',
        description: group.groupDescription,
        memberCount: 1,
        ownerCount: 1,
        pendingMembershipRequests: 0,
        accessType:
            group.groupPrivacy === GroupAccessType.Public
                ? UnifiedGroupAccessType.Public
                : UnifiedGroupAccessType.Private,
        isMember: true,
        isOwner: true,
        isFavorite: false,
        relevancyScore: Number.MAX_VALUE,
        isFollowed: null,
        isExpiring: false,
        expiryDateTime: null,
        lastVisitedTime: new Date(),
        whenSoftDeletedTime: null,
        joinedDateTime: new Date(),
        isLoading: true,
        isJoinRequestPendingApproval: false,
        joinGroupUrl: null,
        isMembershipDynamic: false,
        allowAddGuests: false,
        restoreStatus: UnifiedGroupRestoreStatus.NONE,
        isSubscriptionEnabled: true,
        mailboxGuid: null,
        externalDirectoryOrganizationId: null,
        exportStatus: UnifiedGroupExportStatus.None,
        exportDownloadLink: undefined,
        routingKey: undefined,
        groupKind: undefined,
        addMembersForFamilyUrl: undefined,
        leaveGroupForFamilyUrl: undefined,
        mailboxCultureName: group.pickedLanguageKey,
        autoSubscribeNewMembers: group.groupMembersAreAutoSubscribed,
        allowExternalSenders: group.allowExternalSenders,
        allowExportGroup: undefined,
    };
}

export function convertGroupToUnifiedGroup(group: Group): UnifiedGroup {
    return {
        type: PersonType.Group,
        hubPersonaId: makeHubGroupPersonaId(
            group.externalDirectoryObjectId ? group.externalDirectoryObjectId : '',
            group.mail ? group.mail.toLowerCase() : ''
        ),
        personaId: undefined,
        displayName: group.displayName,
        externalDirectoryObjectId: group.externalDirectoryObjectId
            ? group.externalDirectoryObjectId
            : '',
        emailAddress: group.mail ? group.mail.toLowerCase() : '',
        description: group.description ? group.description : '',
        memberCount: 1,
        ownerCount: 1,
        pendingMembershipRequests: 0,
        accessType:
            group.visibility === 'Public'
                ? UnifiedGroupAccessType.Public
                : UnifiedGroupAccessType.Private,
        isMember: true,
        isOwner: true,
        isFavorite: false,
        relevancyScore: Number.MAX_VALUE,
        isFollowed: null,
        isExpiring: false,
        expiryDateTime: null,
        lastVisitedTime: new Date(),
        whenSoftDeletedTime: null,
        joinedDateTime: new Date(),
        isLoading: true,
        isJoinRequestPendingApproval: false,
        joinGroupUrl: null,
        isMembershipDynamic: false,
        allowAddGuests: false,
        restoreStatus: UnifiedGroupRestoreStatus.NONE,
        isSubscriptionEnabled: true,
        mailboxGuid: null,
        externalDirectoryOrganizationId: null,
        exportStatus: UnifiedGroupExportStatus.None,
        exportDownloadLink: undefined,
        routingKey: undefined,
        groupKind: undefined,
        addMembersForFamilyUrl: undefined,
        leaveGroupForFamilyUrl: undefined,
        mailboxCultureName: group.language,
        autoSubscribeNewMembers: group.autoSubscribeMembersEnabled,
        allowExternalSenders: group.allowExternalSenders,
        allowExportGroup: undefined,
    };
}
